import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOut, ArrowRight } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useSignOutAndGoHome } from "@/hooks/useSignOutAndGoHome";
import { getAccessFlowLock } from "@/lib/access-flow";
import { Button } from "@/components/ui/button";

export const isAccountEntry = (pathname: string) =>
  ["/auth", "/admin/login", "/entrar-bar"].includes(pathname);

export default function ActiveAccountEntryGate({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const signOut = useSignOutAndGoHome();
  const [leaving, setLeaving] = useState(false);
  // Sessions created by the entry form on this same page (password + MFA) must
  // finish in place; only accounts already connected when the page opened are
  // asked to sign out first. Server checks still decide every access.
  const signedOutHere = useRef<string | null>(null);
  useEffect(() => {
    if (!loading && !user) signedOutHere.current = pathname;
  }, [loading, user, pathname]);
  if (!isAccountEntry(pathname)) return <>{children}</>;
  if (loading) return null;
  if (!user) {
    signedOutHere.current = pathname;
    return <>{children}</>;
  }
  if (signedOutHere.current === pathname) return <>{children}</>;
  const flow = getAccessFlowLock();
  const destination = flow === "admin" ? "/admin" : flow === "venue" ? "/venue-management" : "/role-select";
  return (
    <div className="min-h-dvh bg-background text-foreground flex items-center justify-center px-6">
      <section className="max-w-sm space-y-5 text-center" role="alert">
        <h1 className="text-xl font-display font-semibold">Uma conta já está conectada</h1>
        <p className="text-sm text-muted-foreground">Para entrar com outro e-mail ou acessar outro ambiente, primeiro toque em Sair da conta.</p>
        <div className="flex flex-col gap-3">
          <Button disabled={leaving} onClick={() => navigate(destination, { replace: true })}><ArrowRight />Continuar na conta atual</Button>
          <Button variant="outline" disabled={leaving} onClick={async () => { setLeaving(true); try { await signOut(); } finally { setLeaving(false); } }}><LogOut />{leaving ? "Saindo…" : "Sair da conta"}</Button>
        </div>
      </section>
    </div>
  );
}
